import { load } from '@iheartjane/utils';
import type { RouteObject } from 'react-router';
import { Navigate, useLocation, useParams } from 'react-router';

import { MenuSpecialsRedirect } from '@jane/shared-ecomm/pages';
import { Loader } from '@jane/shared/components';

import { FramelessEmbedAppContainer } from './components/framelessEmbedAppContainer';

const Checkout = Loader(
  load(() => import('@jane/monolith/components/checkout/checkout'), 'Checkout')
);
const EmbeddedCart = Loader(
  load(
    () => import('@jane/monolith/components/cart/embeddedCart'),
    'EmbeddedCart'
  )
);
const Menu = Loader(
  load(() => import('@jane/monolith/components/menu'), 'Menu'),
  'menu'
);
const MenuBrandSpecialDetail = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuBrandSpecialDetail')
);
const MenuBrandSpecials = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuBrandSpecials')
);
const MenuFeatured = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuFeatured')
);
const MenuIndex = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuIndex')
);
const MenuProductDetail = Loader(
  load(
    () => import('@jane/monolith/components/menuProductDetail/provider'),
    'MenuProductProvider'
  ),
  'menu-product-detail'
);
const MenuProducts = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuProducts')
);
const MenuSpecialDetail = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuSpecialDetail')
);
const MenuSpecialDetailRedirect = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuSpecialDetailRedirect')
);
const MenuSpecials = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuSpecials')
);
const EnsureNotLoggedInContainer = Loader(
  load(
    () => import('@jane/monolith/components/ensureNotLoggedInContainer'),
    'EnsureNotLoggedInContainer'
  )
);
const ForgotPassword = Loader(
  load(
    () => import('@jane/monolith/components/login/forgotPassword'),
    'ForgotPassword'
  )
);
const Identification = Loader(
  load(
    () => import('@jane/monolith/components/profile/identification'),
    'Identification'
  )
);
const Login = Loader(
  load(() => import('@jane/monolith/components/login'), 'Login')
);
const Logout = Loader(
  load(() => import('@jane/monolith/components/logout'), 'Logout')
);
const ProductReview = Loader(
  load(
    () => import('@jane/monolith/components/productReviewForm'),
    'ProductReview'
  )
);
const Profile = Loader(
  load(() => import('@jane/monolith/components/profile'), 'Profile')
);
const ProfileChangePassword = Loader(
  load(
    () => import('@jane/monolith/components/profile/changePassword'),
    'ProfileChangePassword'
  )
);
const ProfileEdit = Loader(
  load(
    () => import('@jane/monolith/components/profile/edit/profileEdit'),
    'ProfileEdit'
  )
);
const Registration = Loader(
  load(
    () => import('@jane/monolith/components/registration/container'),
    'Registration'
  )
);
const ReplaceCart = Loader(
  load(() => import('@jane/monolith/components/replaceCart'), 'ReplaceCart')
);
const RequiresAuthentication = Loader(
  load(
    () => import('@jane/monolith/components/requiresAuthentication'),
    'RequiresAuthentication'
  )
);
const ReservationDetail = Loader(
  load(
    () => import('@jane/monolith/components/reservationDetail'),
    'ReservationDetail'
  )
);
const ReservationHistory = Loader(
  load(
    () => import('@jane/monolith/components/profile/reservationHistory'),
    'ReservationHistory'
  )
);
const ReservationReceipt = Loader(
  load(
    () => import('@jane/monolith/components/reservationReceipt'),
    'ReservationReceipt'
  )
);
const ResetPassword = Loader(
  load(() => import('@jane/monolith/components/resetPassword'), 'ResetPassword')
);

const Redirect = ({
  basePath,
  isPartnerHosted,
}: {
  basePath: string;
  isPartnerHosted: boolean;
}) => {
  const params = useParams();
  const location = useLocation();
  /**
   * Support for legacy deep linking scripts
   * Partners may still be using old scripts, do not remove.
   */
  if (location.pathname.includes('path=')) {
    const adjustedPath = location.pathname.replace('path=', '');
    return <Navigate to={`${adjustedPath}/${location.search}`} replace />;
  }
  /** ------------------------------------------ */
  return (
    <Navigate
      replace
      to={
        isPartnerHosted
          ? `${basePath}/menu/${location.search}`
          : `/embed/stores/${params['store_id']}/menu/${location.search}`
      }
    />
  );
};

export const buildRoutes = (
  isPartnerHosted: boolean,
  partnerHostedPath: string = ''
): RouteObject[] => {
  if (!isPartnerHosted) return [];

  return [
    {
      path: `${partnerHostedPath}/`,
      element: <FramelessEmbedAppContainer />,
      children: [
        {
          path: `${partnerHostedPath}/`,
          element: <Menu />,
          children: [
            {
              index: true,
              element: <MenuIndex />,
            },
          ],
        },
        {
          path: `${partnerHostedPath}/menu`,
          element: <Menu />,
          children: [
            {
              index: true,
              element: <MenuIndex />,
            },
            {
              path: 'featured',
              element: <MenuFeatured restoreScroll />,
            },
            {
              path: ':category',
              element: <MenuProducts restoreScroll />,
            },
            {
              path: ':category/:featured_category',
              element: <MenuProducts restoreScroll />,
            },
            {
              path: 'rows/:rowId/:slug',
              element: <MenuProducts restoreScroll />,
            },
            {
              path: 'jane-gold',
              element: <MenuBrandSpecials restoreScroll />,
            },
            {
              path: 'jane-gold/:brand_special_id/:slug',
              element: <MenuBrandSpecialDetail />,
            },
            {
              path: 'specials',
              element: <MenuSpecials restoreScroll />,
            },
            {
              path: 'specials/:special_id/:slug',
              element: <MenuSpecialDetail />,
            },
          ],
        },
        {
          path: `${partnerHostedPath}/menu/products/:product_id/:slug`,
          element: <MenuProductDetail />,
        },
        {
          path: `${partnerHostedPath}/products/:product_id/:slug`,
          element: <MenuProductDetail />,
        },
        {
          path: `${partnerHostedPath}/products/:product_id/:slug/reviews/new`,
          element: (
            <RequiresAuthentication>
              <ProductReview />
            </RequiresAuthentication>
          ),
        },
        {
          path: `${partnerHostedPath}/logout`,
          element: <Logout />,
        },
        {
          path: `${partnerHostedPath}/cart`,
          element: <EmbeddedCart />,
        },
        {
          path: `${partnerHostedPath}/cart/checkout`,
          element: (
            <RequiresAuthentication>
              <Checkout />
            </RequiresAuthentication>
          ),
        },
        {
          path: `${partnerHostedPath}/cart/guest_checkout`,
          element: <Checkout />,
        },
        {
          path: `${partnerHostedPath}/reservation_receipt/:reservation_id`,
          element: <ReservationReceipt />,
        },
        {
          path: `${partnerHostedPath}/replaceCart`,
          element: <ReplaceCart />,
        },
        {
          path: `${partnerHostedPath}/login`,
          element: (
            <EnsureNotLoggedInContainer>
              <Login />
            </EnsureNotLoggedInContainer>
          ),
        },
        {
          path: `${partnerHostedPath}/register`,
          element: (
            <EnsureNotLoggedInContainer>
              <Registration />
            </EnsureNotLoggedInContainer>
          ),
        },
        {
          path: `${partnerHostedPath}/forgot-password`,
          element: <ForgotPassword />,
        },
        {
          path: `${partnerHostedPath}/reset-password/:token`,
          element: <ResetPassword />,
        },
        {
          path: `${partnerHostedPath}/specials`,
          element: <MenuSpecialsRedirect />,
        },
        {
          path: `${partnerHostedPath}/specials/:special_id`,
          element: <MenuSpecialDetailRedirect />,
        },
        {
          path: `${partnerHostedPath}/specials/:special_id/:slug`,
          element: <MenuSpecialDetailRedirect />,
        },
        {
          path: `${partnerHostedPath}/profile`,
          element: (
            <RequiresAuthentication>
              <Profile embeddedMode />
            </RequiresAuthentication>
          ),
          children: [
            {
              path: `${partnerHostedPath}/profile/reservations`,
              element: <ReservationHistory />,
            },
            {
              path: `${partnerHostedPath}/profile/reservations/:reservation_id`,
              element: <ReservationDetail />,
            },
            {
              path: `${partnerHostedPath}/profile/identification`,
              element: <Identification />,
            },
            {
              path: `${partnerHostedPath}/profile/edit`,
              element: <ProfileEdit />,
            },
            {
              path: `${partnerHostedPath}/profile/password`,
              element: <ProfileChangePassword />,
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: (
        <Redirect
          isPartnerHosted={isPartnerHosted}
          basePath={partnerHostedPath}
        />
      ),
    },
  ];
};
